import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const useOtpSaldosOperacion = ({ bankState, bankAccounts, bankCycle }) => {
  const [cvuId, setCvuId] = useState(null);
  const [config, setConfig] = useState(null);
  const [configReady, setConfigReady] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const pending = useRef(null);

  const cvus = useMemo(() => {
    const byId = new Map();
    (bankAccounts || []).filter(a => a.bank_key === '1touchpay' && a.cvu_id != null && a.status !== 'deleted')
      .forEach(a => byId.set(Number(a.cvu_id), { ...a, cvu: a.cbu_cvu }));
    Object.entries(bankState?.accounts || {}).forEach(([cvu, account]) => {
      if (account.cvu_id != null) byId.set(Number(account.cvu_id), { ...byId.get(Number(account.cvu_id)), ...account, cvu });
    });
    return [...byId.values()];
  }, [bankState, bankAccounts]);

  useEffect(() => {
    const abort = new AbortController();
    axios.get(`${API}/bancos/1touchpay/operaciones`, { ...auth(), signal: abort.signal, timeout: 15000 })
      .then(r => { if (!abort.signal.aborted) setConfig(r.data); })
      .catch(() => {})
      .finally(() => { if (!abort.signal.aborted) setConfigReady(true); });
    return () => abort.abort();
  }, []);

  useEffect(() => {
    if (!configReady || !cvus.length) return;
    if (cvuId != null && cvus.some(c => Number(c.cvu_id) === cvuId)) return;
    const preferred = Number(config?.cvu_payouts_default);
    const chosen = cvus.find(c => Number(c.cvu_id) === preferred)
      || [...cvus].sort((a, b) => Number(b.balance ?? b.real_balance ?? 0) - Number(a.balance ?? a.real_balance ?? 0))[0];
    setCvuId(Number(chosen.cvu_id));
  }, [configReady, config, cvus, cvuId]);

  const load = useCallback(async (id) => {
    if (id == null) return;
    pending.current?.abort();
    const abort = new AbortController();
    pending.current = abort;
    const sequence = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/bancos/1touchpay/saldos-operacion`, {
        ...auth(), params: { cvu_id: id }, signal: abort.signal, timeout: 20000,
      });
      if (abort.signal.aborted || sequence !== requestId.current) return;
      if (Number(response.data.cvu_id) !== id) throw new Error('La respuesta no corresponde al CVU seleccionado');
      setResult(response.data);
    } catch (e) {
      if (!abort.signal.aborted && sequence === requestId.current) {
        const detail = e.response?.data?.detail;
        setError({ cvuId: id, message: typeof detail === 'string' ? detail : (e.message || 'No se pudieron actualizar los libros') });
      }
    } finally {
      if (!abort.signal.aborted && sequence === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(cvuId);
    return () => { ++requestId.current; pending.current?.abort(); };
  }, [cvuId, bankCycle, load]);

  const selectCvu = useCallback(id => {
    ++requestId.current;
    pending.current?.abort();
    setError(null);
    setCvuId(id);
  }, []);
  const data = result != null && Number(result.cvu_id) === cvuId ? result : null;
  return { cvus, cvuId, selectCvu, data, loading, error: error?.cvuId === cvuId ? error.message : '', configReady };
};